import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Crown, Zap, LogOut, CreditCard, TrendingUp, Download, FileText, Star } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Account() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Please sign in to view your account');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
    fetchSubscriptionStatus();
  }, [fetchUserProfile, fetchSubscriptionStatus]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getTierInfo = (tier) => {
    const tiers = {
      free: {
        name: 'Free Forever',
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        aiLimit: 5,
        features: ['5 AI generations/month', 'Access to free resources', 'Basic teaching tips']
      },
      parent: {
        name: 'Parent',
        color: 'text-pink-600',
        bgColor: 'bg-pink-100',
        aiLimit: 50,
        features: ['50 AI generations/month', 'Parent Portal access', 'Activity Suggester', 'Topic Explainer', 'Educational resources']
      },
      plus: {
        name: 'Teacher Plus',
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        aiLimit: 200,
        features: ['200 AI generations/month', 'All premium resources', 'Priority support', 'Advanced teaching tips']
      },
      pro: {
        name: 'Teacher Pro',
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        aiLimit: 999999,
        features: ['Unlimited AI generations', 'All premium resources', 'Priority support', 'Early access to new features']
      }
    };
    return tiers[tier] || tiers.free;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading your account...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const tierInfo = getTierInfo(user.subscription_tier);
  const aiUsagePercent = user.subscription_tier === 'pro' ? 0 : (user.ai_generations_used / tierInfo.aiLimit) * 100;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-20 h-20 rounded-full border-4 border-blue-100"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                    <span className="text-white text-2xl font-bold">{user.name?.charAt(0)}</span>
                  </div>
                )}
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">{user.name}</h1>
                  <p className="text-base text-slate-600">{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-4 py-2 rounded-lg transition-all inline-flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>

          {/* Current Plan */}
          <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Current Plan</h2>
                <div className="flex items-center gap-3">
                  <span className={`text-2xl font-bold ${tierInfo.color}`}>
                    {tierInfo.name}
                  </span>
                  {user.subscription_tier !== 'free' && (
                    <Crown className={`w-6 h-6 ${tierInfo.color}`} />
                  )}
                </div>
              </div>
              {user.subscription_tier === 'free' && (
                <Link
                  to="/pricing"
                  className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95"
                >
                  Upgrade Plan
                </Link>
              )}
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {tierInfo.features.map((feature) => (
                <div key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                  {feature}
                </div>
              ))}
            </div>

            {user.subscription_tier !== 'free' && subscriptionData && (
              <div className={`${tierInfo.bgColor} rounded-xl p-4 mt-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className={`w-5 h-5 ${tierInfo.color}`} />
                  <span className={`font-semibold ${tierInfo.color}`}>
                    {subscriptionData.billing_period === 'annual' ? 'Annual Subscription' : 'Monthly Subscription'}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Status: </span>
                    <span className={`font-semibold ${
                      subscriptionData.subscription_status === 'active' ? 'text-green-600' :
                      subscriptionData.subscription_status === 'expiring_soon' ? 'text-yellow-600' :
                      subscriptionData.subscription_status === 'expired' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {subscriptionData.subscription_status === 'active' ? 'Active' :
                       subscriptionData.subscription_status === 'expiring_soon' ? 'Expiring Soon' :
                       subscriptionData.subscription_status === 'expired' ? 'Expired' :
                       subscriptionData.subscription_status === 'pending' ? 'Payment Pending' : 'Free'}
                    </span>
                  </div>
                  {subscriptionData.subscription_expiry_date && (
                    <div>
                      <span className="text-slate-500">Valid Until: </span>
                      <span className="font-semibold text-slate-900">
                        {new Date(subscriptionData.subscription_expiry_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      {subscriptionData.days_until_expiry !== undefined && subscriptionData.days_until_expiry >= 0 && (
                        <span className="text-slate-500 ml-2">
                          ({subscriptionData.days_until_expiry} days left)
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Expiry Warnings */}
                {subscriptionData.days_until_expiry !== undefined && subscriptionData.days_until_expiry <= 7 && subscriptionData.days_until_expiry >= 0 && (
                  <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800 font-medium">
                      ⚠️ Your membership expires in {subscriptionData.days_until_expiry} days. Renew now to continue accessing premium resources.
                    </p>
                    <Link
                      to="/payment?plan=plus"
                      className="mt-2 inline-block bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-700 transition-all"
                    >
                      Renew Membership
                    </Link>
                  </div>
                )}
                
                {subscriptionData.days_until_expiry !== undefined && subscriptionData.days_until_expiry < 0 && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800 font-medium">
                      ❌ Your membership has expired. Renew now to restore access to premium resources.
                    </p>
                    <Link
                      to="/payment?plan=plus"
                      className="mt-2 inline-block bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-all"
                    >
                      Renew Now
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subscription Details (for paid users) */}
          {user.subscription_tier !== 'free' && subscriptionData && (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8 shadow-sm">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">My Subscription</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Current Plan</p>
                  <p className="text-lg font-semibold text-slate-900">{tierInfo.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Billing Period</p>
                  <p className="text-lg font-semibold text-slate-900 capitalize">
                    {subscriptionData.billing_period || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Payment Method</p>
                  <p className="text-lg font-semibold text-slate-900">
                    {subscriptionData.payment_method === 'airtel_money' ? 'Airtel Money' :
                     subscriptionData.payment_method === 'upi' ? 'UPI' : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Status</p>
                  <p className={`text-lg font-semibold ${
                    subscriptionData.subscription_status === 'active' ? 'text-green-600' :
                    subscriptionData.subscription_status === 'expiring_soon' ? 'text-yellow-600' :
                    subscriptionData.subscription_status === 'expired' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {subscriptionData.subscription_status === 'active' ? 'Active ✓' :
                     subscriptionData.subscription_status === 'expiring_soon' ? 'Expiring Soon' :
                     subscriptionData.subscription_status === 'expired' ? 'Expired' :
                     subscriptionData.subscription_status === 'pending' ? 'Payment Pending Verification' : 'Free'}
                  </p>
                </div>
              </div>

              {/* Payment History */}
              {subscriptionData.payment_history && subscriptionData.payment_history.length > 0 && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Payment History</h3>
                  <div className="space-y-3">
                    {subscriptionData.payment_history.slice(0, 5).map((payment) => (
                      <div key={payment.payment_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-slate-900">
                            {payment.plan === 'plus' ? 'Teacher Plus' : 'Teacher Pro'} - {payment.billing_period}
                          </p>
                          <p className="text-sm text-slate-600">
                            {new Date(payment.submission_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-slate-900">
                            {payment.currency} {payment.amount}
                          </p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            payment.payment_status === 'approved' ? 'bg-green-100 text-green-800' :
                            payment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {payment.payment_status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AI Usage */}
          <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Zap className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-slate-900">AI Generations This Month</h2>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-3xl font-bold text-slate-900">
                  {user.ai_generations_used}
                </span>
                <span className="text-lg text-slate-600">
                  {user.subscription_tier === 'pro' ? 'Unlimited' : `of ${tierInfo.aiLimit}`}
                </span>
              </div>
              
              {user.subscription_tier !== 'pro' && (
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all ${
                      aiUsagePercent > 80 ? 'bg-red-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${Math.min(aiUsagePercent, 100)}%` }}
                  ></div>
                </div>
              )}
            </div>

            {user.subscription_tier === 'free' && user.ai_generations_used >= 5 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800 mb-2">
                  <strong>You've reached your monthly limit!</strong>
                </p>
                <p className="text-sm text-amber-700 mb-3">
                  Upgrade to Teacher Plus for 200 generations per month or Teacher Pro for unlimited access.
                </p>
                <Link
                  to="/pricing"
                  className="text-sm font-semibold text-amber-800 hover:text-amber-900 underline"
                >
                  View upgrade options →
                </Link>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-slate-400" />
                <h3 className="text-lg font-semibold text-slate-900">Resources Access</h3>
              </div>
              <p className="text-3xl font-bold text-slate-900 mb-1">
                {user.subscription_tier === 'free' ? 'Free Only' : 'All Premium'}
              </p>
              <p className="text-sm text-slate-600">
                {user.subscription_tier === 'free' ? 'Upgrade to access premium worksheets and lesson plans' : 'Full access to all downloadable resources'}
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-slate-400" />
                <h3 className="text-lg font-semibold text-slate-900">Member Since</h3>
              </div>
              <p className="text-3xl font-bold text-slate-900 mb-1">
                {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </p>
              <p className="text-sm text-slate-600">
                Thank you for being part of Learn with Zeenat!
              </p>
            </div>
          </div>

          {/* Teacher Pro Feature: Assessment Hub */}
          {user.subscription_tier === 'pro' && (
            <div className="bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl p-6 text-white shadow-lg mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-6 h-6" />
                    <h3 className="text-xl font-bold">Assessment Hub ⭐</h3>
                  </div>
                  <p className="text-purple-100 mb-1">AI-powered test marking with teacher approval</p>
                  <p className="text-sm text-purple-200">Save hours of grading time on classroom assessments</p>
                </div>
                <Link
                  to="/assessment"
                  className="bg-white text-purple-600 hover:bg-purple-50 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 whitespace-nowrap"
                >
                  Open Assessment Hub
                </Link>
              </div>
            </div>
          )}

          {/* Admin Badge */}
          {user.is_admin && (
            <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold mb-2">Admin Access</h3>
                  <p className="text-purple-100">You have administrative privileges</p>
                </div>
                <Link
                  to="/admin"
                  className="bg-white text-purple-600 hover:bg-purple-50 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95"
                >
                  Go to Admin Dashboard
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default Account;
